import { useQuery, useReactiveVar } from '@apollo/client';
import { ExpandLess } from '@mui/icons-material';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { useNavigate, useParams } from '@tanstack/react-router';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { HOME_PATH, LIBRARY_ASSET_ACTIONS, PROXY_FORMAT_SLUG } from 'src/constants';
import { buildAllFieldsQ, buildAllMFQ } from 'src/dynamoQueries/queryHelpers';
import styles from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/AkomiMediaCard/MediaCardHorizontalDetails/MediaCardHorizontalDetailsStyles';
import MediaCardHorizontalTab from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/AkomiMediaCard/MediaCardHorizontalDetails/MediaCardHorizontalTab/MediaCardHorizontalTab';
import { cleanPayload, getMetadataFieldValue } from 'src/helpers/common';
import horizontalMediaCardPosition from 'src/helpers/horizontalMediaCardPosition';
import widthToBreakPoint from 'src/helpers/widthToBreakpoint';
import viewStateVar from 'src/store/viewState';
import type Player from 'video.js/dist/types/player';
import type { GalleryRouteParams, MediaCardBreakPoints, MediaCardHorizontalDetailsProps } from '../../types';
import { AssetActionsPermMenu } from '../MediaCardActionsIcon/AssetActionsPermMenu';
import MediaRouterComponent from '../MediaRouterComponent';

type BreakpointKey = keyof MediaCardBreakPoints;

const MediaCardHorizontalDetails: React.FC<MediaCardHorizontalDetailsProps> = (props) => {
  const classes = styles();
  const viewState = useReactiveVar(viewStateVar);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<Player | null>(null);

  const closeHorizontalMediaCard = (): void => {
    viewStateVar({ ...viewState, showHorizontal: false });
  };

  const {
    data: proxyFormatData,
    loading,
    error,
  } = useQuery(buildAllFieldsQ(), {
    skip: !viewState.showHorizontal || !viewState.horizontalMediaCardDetails.id,
    variables: { asset_Id: viewState.horizontalMediaCardDetails.id, fmtType_Slug: PROXY_FORMAT_SLUG },
    nextFetchPolicy: 'cache-only',
  });

  const cleanedData = cleanPayload(proxyFormatData) as Array<{ id?: string }>;
  const proxyFormat = cleanedData[0] || {};

  const { loading: loadingFormatData, error: formatDataError } = useQuery(buildAllMFQ(), {
    skip: !proxyFormat?.id,
    variables: { globalId: proxyFormat?.id, definition_Name_In: 'mimetype' },
    nextFetchPolicy: 'cache-only',
  });

  const {
    data: metadata,
    loading: loadingMetadata,
    error: metadataError,
  } = useQuery(buildAllMFQ(), {
    skip: !viewState.horizontalMediaCardDetails.id,
    variables: { globalId: viewState.horizontalMediaCardDetails.id, definition_Name_In: 'name,inpoint' },
    nextFetchPolicy: 'cache-only',
  });

  const allLoading = loading || loadingFormatData || loadingMetadata;
  const allError = error || formatDataError || metadataError;

  const params = useParams({ strict: false }) as GalleryRouteParams;
  const inpoint = getMetadataFieldValue(metadata, 'inpoint') as string | undefined;

  const navigate = useNavigate();

  useEffect(() => {
    if (viewState.showHorizontal) {
      const breakpointKey = widthToBreakPoint(props.windowWidth) as BreakpointKey;
      const breakpointValue = props.mediaCardBreakPoints[breakpointKey];
      const itemsInRow = 12 / (breakpointValue as number);
      const mediaCardIndex = viewState.horizontalMediaCardIndex;
      if (elementRef.current && Math.ceil((mediaCardIndex + 1) / itemsInRow) - 1 !== 0) {
        window.scrollTo({
          top: elementRef.current.offsetTop - 80,
          left: 0,
          behavior: 'smooth',
        });
      } else if (elementRef.current && Math.ceil((mediaCardIndex + 1) / itemsInRow) - 1 === 0) {
        window.scrollTo({
          top: elementRef.current.offsetTop - 450,
          left: 0,
          behavior: 'smooth',
        });
      }
    }
  }, [viewState.horizontalMediaCardIndex, viewState.showHorizontal, props.windowWidth, props.mediaCardBreakPoints]);

  const _titleClickHandler = (): void => {
    const clipId = viewState.horizontalMediaCardDetails.id;

    navigate({ to: `${HOME_PATH}/media-page/${clipId}` });
    viewStateVar({
      ...viewState,
      mediaCardMediaPageIndex: props.mediaCardMediaPageIndex,
      mediaPageViaPlaylist: false,
      mediaPageViaLibrary: true,
    });
  };

  const setPlayer = (_player: Player): void => {
    playerRef.current = _player;
  };

  const breakpointKey = widthToBreakPoint(props.windowWidth) as BreakpointKey;
  const breakpointValue = props.mediaCardBreakPoints[breakpointKey];
  const itemsInRow = 12 / (breakpointValue as number);
  const expectedPosition = horizontalMediaCardPosition(viewState.horizontalMediaCardIndex, itemsInRow, props.maxIndex);

  if (viewState.showHorizontal && props.cardIndex === expectedPosition && !allLoading && !allError) {
    return (
      <Grid item xs={12}>
        <Container maxWidth={false} className={classes.horizontalView} ref={elementRef}>
          <Card className={classes.root}>
            <div className={classes.media}>
              <AssetActionsPermMenu
                itemId={viewState.horizontalMediaCardDetails.id || ''}
                moreActions={!params.galleryId ? LIBRARY_ASSET_ACTIONS : []}
              />
              <MediaRouterComponent
                itemId={viewState.horizontalMediaCardDetails.id || ''}
                setPlayer={setPlayer}
                akomiMediaCardIndex={props.cardIndex}
              />
            </div>
            <div className={classes.details}>
              <MediaCardHorizontalTab
                videoInfo={{
                  df: inpoint?.search(';') !== -1,
                }}
                player={playerRef.current || undefined}
                itemId={viewState.horizontalMediaCardDetails.id || ''}
              />
              <div className={classes.button}>
                <Button variant="text" color="inherit" size="small" onClick={closeHorizontalMediaCard}>
                  HIDE PREVIEW
                  <ExpandLess />
                </Button>
              </div>
            </div>
          </Card>
        </Container>
      </Grid>
    );
  }

  return null;
};

export default MediaCardHorizontalDetails;
